import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import {
  getProcessingConfiguration,
  updateProcessingConfiguration,
} from "../api/workerApi";
import { friendlyMessageForApiError } from "../api/errorUtils";
import { Hero } from "../components/adminSurface/AdminSurface";
import { GearArt } from "../components/adminSurface/heroArt";

export default function ProcessingConfiguration() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [config, setConfig] = useState({
    maxConcurrentBatches: 5,
    batchPollingIntervalSeconds: 30,
    workerTimeoutMinutes: 60,
    enableAutoRetry: true,
    maxRetryAttempts: 3,
    retryDelaySeconds: 300,
    postScriptEncoding: "UTF-8",
    enableTurkishEncoding: false,
    compressionLevel: "Optimal",
    enableDetailedLogging: false,
    enableArchiveDocumentLogging: false,
    archiveChunkSizeKB: 1024,
    hostAffinityMode: "Reachable",
  });
  const [originalConfig, setOriginalConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!token) return;
    fetchConfiguration();
  }, [token]);

  const fetchConfiguration = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getProcessingConfiguration(token);
      setConfig(data || config);
      setOriginalConfig(data || config);
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await updateProcessingConfiguration(token, config);
      setSuccess("Processing configuration saved successfully!");
      setOriginalConfig(config);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(config) !== JSON.stringify(originalConfig);

  return (
    <div className="app-root">
      <div className="nav-strip">
        <NavBar />
      </div>
      <HeaderBar />
      <div className="page-container">
        {/* Header */}
        <Hero
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
              strokeLinecap="round" strokeLinejoin="round" style={{ color: "#fff" }}>
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          }
          title={t("processing.configuration", { defaultValue: "Processing Configuration" })}
          subtitle="Configure batch processing behavior, encoding, and performance settings"
          art={<GearArt />}
        />

        {loading && (
          <div
            style={{
              textAlign: "center",
              padding: "48px 0",
              color: "#666",
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                border: "3px solid #e0e0e0",
                borderTop: "3px solid #2563eb",
                borderRadius: "50%",
                display: "inline-block",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ marginTop: 12 }}>Loading configuration...</p>
          </div>
        )}

        {!loading && (
          <div>
            {/* Worker Settings */}
            <div className="config-card" style={{ marginBottom: 20 }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #e5e7eb",
                  backgroundColor: "#fafafa",
                }}
              >
                <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
                  Worker Settings
                </h2>
              </div>
              <div style={{ padding: 0 }}>
                <div
                  style={{
                    padding: "16px 24px",
                    backgroundColor: "#f9fafb",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          display: "block",
                          marginBottom: 4,
                        }}
                      >
                        Max Concurrent Batches
                      </label>
                      <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
                        Number of batches processed simultaneously
                      </p>
                    </div>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={config.maxConcurrentBatches}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          maxConcurrentBatches: parseInt(e.target.value),
                        })
                      }
                      style={{
                        width: 80,
                        padding: "6px 10px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontSize: 14,
                        textAlign: "center",
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    padding: "16px 24px",
                    backgroundColor: "white",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          display: "block",
                          marginBottom: 4,
                        }}
                      >
                        Polling Interval (seconds)
                      </label>
                      <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
                        How often to check for new batches
                      </p>
                    </div>
                    <input
                      type="number"
                      min="5"
                      max="300"
                      value={config.batchPollingIntervalSeconds}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          batchPollingIntervalSeconds: parseInt(e.target.value),
                        })
                      }
                      style={{
                        width: 80,
                        padding: "6px 10px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontSize: 14,
                        textAlign: "center",
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    padding: "16px 24px",
                    backgroundColor: "#f9fafb",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          display: "block",
                          marginBottom: 4,
                        }}
                      >
                        Worker Timeout (minutes)
                      </label>
                      <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
                        Maximum time allowed for batch processing
                      </p>
                    </div>
                    <input
                      type="number"
                      min="10"
                      max="480"
                      value={config.workerTimeoutMinutes}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          workerTimeoutMinutes: parseInt(e.target.value),
                        })
                      }
                      style={{
                        width: 80,
                        padding: "6px 10px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontSize: 14,
                        textAlign: "center",
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    padding: "16px 24px",
                    backgroundColor: "white",
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 16,
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          display: "block",
                          marginBottom: 4,
                        }}
                      >
                        {t("processing.hostAffinity", {
                          defaultValue: "Multi-node Batch Claiming",
                        })}
                      </label>
                      <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
                        {t("processing.hostAffinityHelp", {
                          defaultValue:
                            "Which pending batches a worker may take when several worker nodes share this database",
                        })}
                      </p>
                    </div>
                    <select
                      value={config.hostAffinityMode || "Reachable"}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          hostAffinityMode: e.target.value,
                        })
                      }
                      style={{
                        minWidth: 260,
                        padding: "6px 10px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontSize: 14,
                        backgroundColor: "white",
                      }}
                    >
                      <option value="Reachable">
                        {t("processing.hostAffinityReachable", {
                          defaultValue: "Any batch whose file it can read (recommended)",
                        })}
                      </option>
                      <option value="Owner">
                        {t("processing.hostAffinityOwner", {
                          defaultValue: "Only batches it registered itself",
                        })}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Retry Settings */}
            <div className="config-card" style={{ marginBottom: 20 }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #e5e7eb",
                  backgroundColor: "#fafafa",
                }}
              >
                <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
                  Retry Settings
                </h2>
              </div>
              <div style={{ padding: 0 }}>
                <div
                  style={{
                    padding: "16px 24px",
                    backgroundColor: "#f9fafb",
                    borderBottom: config.enableAutoRetry
                      ? "1px solid #f0f0f0"
                      : "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <label
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      Enable Automatic Retry on Failure
                    </label>
                    <input
                      type="checkbox"
                      checked={config.enableAutoRetry}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          enableAutoRetry: e.target.checked,
                        })
                      }
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </div>

                {config.enableAutoRetry && (
                  <>
                    <div
                      style={{
                        padding: "16px 24px",
                        backgroundColor: "white",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <label style={{ fontSize: 14, fontWeight: 500 }}>
                          Max Retry Attempts
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={config.maxRetryAttempts}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              maxRetryAttempts: parseInt(e.target.value),
                            })
                          }
                          style={{
                            width: 80,
                            padding: "6px 10px",
                            border: "1px solid #d1d5db",
                            borderRadius: 6,
                            fontSize: 14,
                            textAlign: "center",
                          }}
                        />
                      </div>
                    </div>

                    <div
                      style={{
                        padding: "16px 24px",
                        backgroundColor: "#f9fafb",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <label style={{ fontSize: 14, fontWeight: 500 }}>
                          Retry Delay (seconds)
                        </label>
                        <input
                          type="number"
                          min="60"
                          max="3600"
                          value={config.retryDelaySeconds}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              retryDelaySeconds: parseInt(e.target.value),
                            })
                          }
                          style={{
                            width: 80,
                            padding: "6px 10px",
                            border: "1px solid #d1d5db",
                            borderRadius: 6,
                            fontSize: 14,
                            textAlign: "center",
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Encoding Settings */}
            <div className="config-card" style={{ marginBottom: 20 }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #e5e7eb",
                  backgroundColor: "#fafafa",
                }}
              >
                <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
                  Encoding Settings
                </h2>
              </div>
              <div style={{ padding: 0 }}>
                <div
                  style={{
                    padding: "16px 24px",
                    backgroundColor: "#f9fafb",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          display: "block",
                          marginBottom: 4,
                        }}
                      >
                        PostScript Default Encoding
                      </label>
                      <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
                        Default text encoding for PostScript files
                      </p>
                    </div>
                    <select
                      value={config.postScriptEncoding}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          postScriptEncoding: e.target.value,
                        })
                      }
                      style={{
                        minWidth: 180,
                        padding: "6px 10px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontSize: 14,
                        backgroundColor: "white",
                      }}
                    >
                      <option value="UTF-8">UTF-8 (Universal)</option>
                      <option value="ISO-8859-1">ISO-8859-1 (Latin-1)</option>
                      <option value="Windows-1252">Windows-1252</option>
                    </select>
                  </div>
                </div>

                <div
                  style={{
                    padding: "16px 24px",
                    backgroundColor: "white",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          display: "block",
                          marginBottom: 4,
                          cursor: "pointer",
                        }}
                      >
                        Enable Legacy Turkish Encoding (Windows-1254)
                      </label>
                      <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
                        For backward compatibility with Turkish PostScript files
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={config.enableTurkishEncoding}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          enableTurkishEncoding: e.target.checked,
                        })
                      }
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Settings */}
            <div className="config-card" style={{ marginBottom: 20 }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #e5e7eb",
                  backgroundColor: "#fafafa",
                }}
              >
                <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
                  Performance Settings
                </h2>
              </div>
              <div style={{ padding: 0 }}>
                <div
                  style={{
                    padding: "16px 24px",
                    backgroundColor: "#f9fafb",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          display: "block",
                          marginBottom: 4,
                        }}
                      >
                        Compression Level
                      </label>
                      <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
                        Trade-off between speed and file size
                      </p>
                    </div>
                    <select
                      value={config.compressionLevel}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          compressionLevel: e.target.value,
                        })
                      }
                      style={{
                        minWidth: 180,
                        padding: "6px 10px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontSize: 14,
                        backgroundColor: "white",
                      }}
                    >
                      <option value="Fastest">
                        Fastest (Less compression)
                      </option>
                      <option value="Optimal">Optimal (Balanced)</option>
                      <option value="SmallestSize">
                        Smallest Size (Max compression)
                      </option>
                    </select>
                  </div>
                </div>

                <div
                  style={{
                    padding: "16px 24px",
                    backgroundColor: "white",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          display: "block",
                          marginBottom: 4,
                        }}
                      >
                        Archive Chunk Size (KB)
                      </label>
                      <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
                        Buffer size for reading/writing archives
                      </p>
                    </div>
                    <input
                      type="number"
                      min="256"
                      max="8192"
                      step="256"
                      value={config.archiveChunkSizeKB}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          archiveChunkSizeKB: parseInt(e.target.value),
                        })
                      }
                      style={{
                        width: 100,
                        padding: "6px 10px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontSize: 14,
                        textAlign: "center",
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    padding: "16px 24px",
                    backgroundColor: "#f9fafb",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          display: "block",
                          marginBottom: 4,
                          cursor: "pointer",
                        }}
                      >
                        {t("processing.detailedLogging", {
                          defaultValue: "Enable Detailed Logging",
                        })}
                      </label>
                      <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
                        {t("processing.detailedLoggingHelp", {
                          defaultValue:
                            "Increases worker polling-loop log verbosity (may impact performance)",
                        })}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={config.enableDetailedLogging}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          enableDetailedLogging: e.target.checked,
                        })
                      }
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    padding: "16px 24px",
                    backgroundColor: "white",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          display: "block",
                          marginBottom: 4,
                          cursor: "pointer",
                        }}
                      >
                        {t("processing.archiveDocumentLogging", {
                          defaultValue: "Verbose Archive Logging",
                        })}
                      </label>
                      <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
                        {t("processing.archiveDocumentLoggingHelp", {
                          defaultValue:
                            "Log one line per document and chunk during archiving. Off by default — a 100,000-document batch otherwise produces 100,000+ log lines.",
                        })}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={config.enableArchiveDocumentLogging}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          enableArchiveDocumentLogging: e.target.checked,
                        })
                      }
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div
                style={{
                  padding: 16,
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 6,
                  color: "#991b1b",
                  marginBottom: 20,
                }}
              >
                {error}
              </div>
            )}

            {success && (
              <div
                style={{
                  padding: 16,
                  backgroundColor: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 6,
                  color: "#166534",
                  marginBottom: 20,
                }}
              >
                {success}
              </div>
            )}

            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 12,
                marginTop: 24,
              }}
            >
              <button
                onClick={fetchConfiguration}
                disabled={loading}
                className="cancel-btn"
              >
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="go-btn"
              >
                {saving ? "Saving..." : "Save Configuration"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
